import * as THREE from 'three';
import * as marietta3d from '../../marietta3d';
// import CalculationUtil from '../webcad/util/CalculationUtil';

// Pointクラス,RoomPointクラスを統合
export default class Point {
    public vector: THREE.Vector2;

    public flag: number;

    constructor(x: number, y: number, flag: number = 0) {
      // クラス自体を削除してvector2への置き換えを行うと膨大な修正が必要になるため，クラス内でvector2に変換
      this.vector = new THREE.Vector2(x, y);
      // RoomPointクラスを統合するためフラグを追加
      this.flag = flag;
    }

    // 各座標を更新/返還
    set x(_x: number) {
      this.vector.x = _x;
    }

    get x(): number {
      return this.vector.x;
    }

    set y(_y: number) {
      this.vector.y = _y;
    }

    get y(): number {
      return this.vector.y;
    }

    /*------------------------------------------------------
    * 初期化
    *
    */
    Init() {
      this.vector.x = 0;
      this.vector.y = 0;
      this.flag = 0;
    }

    // 旧計算結果と新(vector2)計算結果のテスト
    static refactoringTester = (result: boolean, message: string) => {
      if (result) return;
      console.error(message);
    }

    /**
     * ２点の距離を計算する
     */
    static distance(p1: Point, p2: Point) {
      // 旧計算方法
      const _x: number = ((p2.x - p1.x) ** 2);
      const _y: number = ((p2.y - p1.y) ** 2);
      const ab = Math.sqrt(_x + _y);

      // 新計算方法
      const distance: number = p1.vector.clone().distanceTo(p2.vector.clone());

      // 計算方法の変更で値に差異が発生していないかテスト
      Point.refactoringTester(ab === distance, `計算結果が異なります/ 旧 ${ab} 新 ${distance} Point.ts 64`);

      p1.distanceTo(p2);

      return distance;
    }

    /**
     * thisからの距離を計算する(RoomPointで使われていた距離算出処理)
     */
    distanceTo(p: Point): number {
      // 旧計算方法
      const dx: number = this.x - p.x;
      const dy: number = this.y - p.y;
      const d = Math.sqrt(dx * dx + dy * dy);

      // 新計算方法
      const vec1: THREE.Vector2 = p.clone().vector.clone();
      const vec2: THREE.Vector2 = this.clone().vector.clone();
      const distanceTo: number = vec1.clone().distanceTo(vec2.clone());

      // 計算方法の変更で値に差異が発生していないかテスト
      Point.refactoringTester(d === distanceTo, `計算結果が異なります/ 旧 ${d} 新 ${distanceTo} Point.ts 86`);

      return distanceTo;
    }

    equals(p:Point):boolean {
      const vec1: THREE.Vector2 = p.clone().vector.clone();
      const vec2: THREE.Vector2 = this.clone().vector.clone();
      return vec2.equals(vec1);
    }

    /**
     * ２点の中間点
     */
    static interpolate(p1: Point, p2: Point, percent: number): Point {
      // 旧計算方法
      const distancex: number = p2.x - p1.x;
      const distancey: number = p2.y - p1.y;
      const retPoint: Point = new Point(p1.x + distancex * percent, p1.y + distancey * percent);

      // 新計算方法
      const lerp = p1.vector.clone().lerp(p2.vector.clone(), percent);

      // 計算方法の変更で値に差異が発生していないかテスト(全ての値がNaNのときは比較がfalseになるためスキップ)
      const message = `計算結果が異なります/ 旧 { x: ${retPoint.x}, y: ${retPoint.y} } 新 { x: ${lerp.x}, y: ${lerp.y} } Point.ts 104`;
      Point.refactoringTester(((retPoint.x === lerp.x) && (retPoint.y === lerp.y)) || (Number.isNaN(retPoint.x) && Number.isNaN(retPoint.y) && Number.isNaN(lerp.x) && Number.isNaN(lerp.y)), message);

      return new Point(lerp.x, lerp.y);
    }

    /**
     * 2点間の角度を求める
     */
    static getAngleBetween(p1: Point, p2: Point): number {
      // 底辺の角度
      const angle: number = Math.atan2(p2.x - p1.x, -(p2.y - p1.y)) * (180 / Math.PI) - 90;

      return angle;
    }

    scaleDiv(num: number) {
      const vec: THREE.Vector2 = this.clone().vector.clone().divideScalar(num);

      this.x = vec.x;
      this.y = vec.y;
    }

    /*------------------------------------------------------
     * method : Point.AngleRad
     *
     * thisを中心として、Ｘ軸＋方向と点 p のなす角を求める。
     * （反時計周り：プラス、戻り値：ラジアン）
     *
     */
    angleRad(p: Point): number {
      let r = Point.distance(this.clone(), p.clone());
      if (r < marietta3d.AutoGen.utils.ALMOST_ZERO) {
        return 0.0;
      }
      const dx = (p.x - this.x);
      const dy = (p.y - this.y);
      r = Math.atan2(dy, dx);
      if (r < 0.0) {
        r += (2 * Math.PI);
      }

      return r;
    }

    /*------------------------------------------------------
     * method : Point.Angle
     *
     * thisを中心として、Ｘ軸＋方向と点 p のなす角を求める。
     * （反時計周り：プラス、戻り値：度）
     *
     */
    angle(p: Point): number {
      const r = this.clone().angleRad(p.clone());

      return ((r / Math.PI) * 180);
    }

    /*------------------------------------------------------
     * method : Point.RotationRad
     *
     * centerを中心として、thisをrad分だけ回転させる
     * （反時計周り：プラス）rad : ラジアン
     *
     */
    rotationRad(center: Point, rad: number) {
      const tx = this.x - center.x;
      const ty = this.y - center.y;
      const c = Math.cos(rad);
      const s = Math.sin(rad);
      this.x = (tx * c - ty * s) + center.x;
      this.y = (tx * s + ty * c) + center.y;
    }

    /*------------------------------------------------------
     * method : Point.Rotation
     *
     * centerを中心として、thisをang分だけ回転させる
     * （反時計周り：プラス）ang : 度
     *
     */
    rotation(center: Point, ang: number) {
      const rad = (ang / 180) * Math.PI; // radian

      this.rotationRad(center, rad);
    }

    /*------------------------------------------------------
     * method : Point.Swap
     * 入れ替え
     */
    swap(p: Point) {
      const point = p;
      [this.x, point.x] = [point.x, this.x];
      [this.y, point.y] = [point.y, this.y];
      [this.flag, point.flag] = [point.flag, this.flag];
    }

    /*------------------------------------------------------
     * method : Point.CopyFrom
     * 値の明示的なコピー
     */
    copyFrom(p: Point) {
      this.x = p.x;
      this.y = p.y;
      this.flag = p.flag;
    }

    /*------------------------------------------------------
     * ベクターの外積  crs＝V1ｘV2
     *
     */
    getVectorProduct(v2: Point): number {
      // 旧計算方法
      const oldResult = (this.x * v2.y - this.y * v2.x);

      // 新計算方法
      const vec1: THREE.Vector2 = v2.clone().vector.clone();
      const vec2: THREE.Vector2 = this.clone().vector.clone();

      const newResult = -(vec1.clone().cross(vec2.clone()));

      // 計算方法の変更で値に差異が発生していないかテスト
      Point.refactoringTester(oldResult === newResult, `計算結果が異なります/ 旧 ${oldResult} 新 ${newResult} Point.ts 227`);

      return newResult;
    }

    /*------------------------------------------------------
     * ベクターの内積  dot＝V1・V2
     *
     */
    getInnerProduct(v2: Point): number {
      // 旧計算方法
      const oldResult = (this.x * v2.x + this.y * v2.y);

      // 新計算方法
      const vec1: THREE.Vector2 = v2.clone().vector.clone();
      const vec2: THREE.Vector2 = this.clone().vector.clone();

      const newResult = vec1.clone().dot(vec2.clone());

      // 計算方法の変更で値に差異が発生していないかテスト
      Point.refactoringTester(oldResult === newResult, `計算結果が異なります/ 旧 ${oldResult} 新 ${newResult} Point.ts 247`);

      return newResult;
    }

    /*------------------------------------------------------
     * ベクターの減算  Ｖ＝V1－V2
     *
     */
    getSubVectorZ2(v2: Point): Point {
      // 旧計算方法
      const oldResult = new Point(this.x - v2.x, this.y - v2.y, 0);

      // 新計算方法
      const vec1: THREE.Vector2 = this.clone().vector.clone();
      const vec2: THREE.Vector2 = v2.clone().vector.clone();
      const sub: THREE.Vector2 = vec1.clone().sub(vec2.clone());
      const newResult = new Point(sub.x, sub.y, 0);

      // 計算方法の変更で値に差異が発生していないかテスト(全ての値がNaNのときは比較がfalseになるためスキップ)
      const message = `計算結果が異なります/ 旧 { x: ${oldResult.x}, y: ${oldResult.y} } 新 { x: ${newResult.x}, y: ${newResult.y} } Point.ts 267`;
      Point.refactoringTester(((oldResult.x === newResult.x) && (oldResult.y === newResult.y)) || (Number.isNaN(oldResult.x) && Number.isNaN(oldResult.y) && Number.isNaN(newResult.x) && Number.isNaN(newResult.y)), message);

      return newResult;
    }

    /*------------------------------------------------------
     * 座標の中点を求める
     *
     */
    getMidPoint(v2: Point): Point {
      // 旧計算方法
      const oldResult = new Point((this.x + v2.x) / 2, (this.y + v2.y) / 2, 0);

      // 新計算方法
      const vec1: THREE.Vector2 = this.clone().vector.clone();
      const vec2: THREE.Vector2 = v2.clone().vector.clone();
      const lerp: THREE.Vector2 = vec1.clone().lerp(vec2.clone(), 0.5);
      const newResult = new Point(lerp.x, lerp.y, 0);

      // 計算方法の変更で値に差異が発生していないかテスト(全ての値がNaNのときは比較がfalseになるためスキップ)
      const message = `計算結果が異なります/ 旧 { x: ${oldResult.x}, y: ${oldResult.y} } 新 { x: ${newResult.x}, y: ${newResult.y} } Point.ts 288`;
      Point.refactoringTester(((oldResult.x === newResult.x) && (oldResult.y === newResult.y)) || (Number.isNaN(oldResult.x) && Number.isNaN(oldResult.y) && Number.isNaN(newResult.x) && Number.isNaN(newResult.y)), message);

      return newResult;
    }

    /*------------------------------------------------------
     * ベクターの大きさを得る  ｄ＝｜Ｖ｜
     *
     */
    getVectorLength(): number {
      // 旧計算方法
      const oldResult = Math.sqrt(this.x * this.x + this.y * this.y);

      // 旧計算方法
      const newResult = Math.sqrt(this.clone().getInnerProduct(this.clone()));

      // 計算方法の変更で値に差異が発生していないかテスト
      Point.refactoringTester(oldResult === newResult, `計算結果が異なります/ 旧 ${oldResult} 新 ${newResult} Point.ts 306`);

      return newResult;
    }

    /*------------------------------------------------------
     * thisベクターの単位化（正規化）。
     * 引数のベクトルを正規化して this に格納する
     * dist＜ALMOST_ZERO  の時、 ゼロベクトル(0,0)の値を返す。
     */
    unitVector(v: Point): number {
      let d = v.clone().getVectorLength();

      if (d < marietta3d.AutoGen.utils.ALMOST_ZERO) {
        d = 0.0;
        this.x = 0.0;
        this.y = 0.0;
      } else {
        this.x = v.x / d;
        this.y = v.y / d;
      }

      return d;
    }

    /*------------------------------------------------------
     * ２点を与えて単位ベクターと距離を求める
     * （thisに単位ベクトルが入る）
     */
    public unitVectorZ2(v1: Point, v2: Point): number {
      const vv = v2.clone().getSubVectorZ2(v1.clone());
      const d = this.clone().unitVector(vv.clone());

      return d;
    }

    /*------------------------------------------------------
     * 単位直線と点との関係値を求める
     *
     *   p: 始点
     *   v:方向ベクトル（単位ベクトル）
     *  this:調べる点
     *  rc:戻り値 rc.x:内積値、rc.y:外積値
     */
    public isPtOnEdgeVector(p: Point, v: Point, rc: Point) {
      const rv = rc;
      const vv = this.clone().getSubVectorZ2(p.clone());
      rv.x = v.clone().getInnerProduct(vv.clone());
      rv.y = v.clone().getVectorProduct(vv.clone());
    }

    /**
     * クローン
     */
    clone(): Point {
      const ret: THREE.Vector2 = this.vector.clone();
      return new Point(ret.clone().x, ret.clone().y, this.flag);
    }
}
